import React, { useState } from "react";
import {
  TextField,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { useWatch, useController } from "react-hook-form";
import * as Yup from "yup";

interface ChipInputFieldProps {
  fieldName: string;
  label: string;
  control: any;
  error?: boolean;
  helperText?: any;
  setValue: (name: string, value: any) => void;
  schema?: Yup.AnySchema;
  setError: any;
  clearError: any;
  placeholder: string;
}

const ChipInputField: React.FC<ChipInputFieldProps> = ({
  fieldName,
  label,
  control,
  error,
  helperText,
  setValue,
  schema,
  setError,
  clearError,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState("");
  const { field } = useController({ name: fieldName, control });
  const chips = useWatch({ control, name: fieldName }) || [];

  const handleDelete = (index: number) => {
    const newChips = chips.filter((_: any, i: number) => i !== index);
    setValue(fieldName, newChips);
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if ((event.key === "Enter" || event.key === ",") && inputValue.trim()) {
      let value = inputValue.trim();
      event.preventDefault();
      if (schema) {
        schema
          ?.validate(value, { abortEarly: false }) // Validate form data
          .then(() => {
            clearError(fieldName);
            setValue(fieldName, [...chips, inputValue.trim()]);
            setInputValue("");
          })
          .catch((error) => {
            error.inner.forEach((err: any) => {
              setError(fieldName, { type: "manual", message: err.message }); // Set errors
            });
          });
      } else {
        setValue(fieldName, [...chips, inputValue.trim()]);
        setInputValue("");
      }
    } else {
    }
  };

  const handleBlur = async () => {
    if (inputValue.trim()) {
      let value = inputValue.trim();
      if (schema) {
        // ... (your validation logic)
        schema
          ?.validate(value, { abortEarly: false })
          .then(() => {
            clearError(fieldName);
            setValue(fieldName, [...chips, value]); // Use 'value' after trim
            setInputValue("");
          })
          .catch((error) => {
            error.inner.forEach((err: any) => {
              setError(fieldName, { type: "manual", message: err.message });
            });
          });
      } else {
        setValue(fieldName, [...chips, value]);
        setInputValue("");
      }
    }
  };

  return (
    <FormControl fullWidth variant="outlined" error={error}>
      <InputLabel shrink sx={{ backgroundColor: "#ffffff", pr: 2 }}>
        {label}
      </InputLabel>
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          padding: 1,
          px: 2,
          alignItems: "center",
          minHeight: 56,
          borderRadius: "4px",
        }}
      >
        {chips.map((chip: string, index: number) => (
          <Chip
            key={index}
            label={chip}
            onDelete={() => handleDelete(index)}
            color="secondary"
          />
        ))}
        <TextField
          variant="standard"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          sx={{ flex: 1, minWidth: 120 }}
          InputProps={{ disableUnderline: true }}
          autoComplete="off"
        />
      </Paper>
      {error && (
        <FormHelperText sx={{ color: "secondary.main" }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ChipInputField;
